import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// Safely initialize the 3-D module for Highcharts
try {
  // eslint-disable-next-line
  const HC3d = require('highcharts/highcharts-3d');
  if (typeof HC3d === 'function') HC3d(Highcharts);
  else if (HC3d && typeof HC3d.default === 'function') HC3d.default(Highcharts);
} catch (e) {
  console.warn('Highcharts 3D module not loaded:', e);
}

const API_BASE = process.env.REACT_APP_API_BASE;

/* ─── Flip-card & Unified Premium UI CSS ─── */
const stylesCSS = `
  /* ---------- flip card scene wrapper ---------- */
  .flip-scene {
    perspective: 1000px;
    height: 200px;      /* fixed height so back face lines up */
  }

  /* ---------- flipper (the rotating element) ---------- */
  .flip-card {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.65s cubic-bezier(0.4, 0.2, 0.2, 1);
    cursor: pointer;
    border-radius: 18px;
  }

  /* hover flips the card */
  .flip-scene:hover .flip-card {
    transform: rotateY(180deg);
  }

  /* ---------- shared face styles ---------- */
  .flip-front,
  .flip-back {
    position: absolute;
    inset: 0;
    border-radius: 18px;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.2rem 0.9rem;
    overflow: hidden;
  }

  /* ---------- FRONT ---------- */
  .flip-front {
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(14px);
    border: 1px solid rgba(255,255,255,0.7);
    box-shadow:
      0 4px 20px rgba(0,0,0,0.08),
      inset 0 1px 0 rgba(255,255,255,0.9);
    text-align: center;
  }

  /* ---------- BACK ---------- */
  .flip-back {
    transform: rotateY(180deg);
    text-align: center;
    box-shadow:
      0 12px 40px rgba(0,0,0,0.25),
      inset 0 1px 0 rgba(255,255,255,0.15);
  }

  /* icon bubble - front */
  .flip-icon {
    width: 62px;
    height: 62px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6rem;
    margin-bottom: 0.65rem;
    box-shadow: 0 6px 18px rgba(0,0,0,0.18);
    flex-shrink: 0;
  }

  /* entrance float-in */
  @keyframes floatUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .flip-scene {
    animation: floatUp 0.5s ease both;
  }

  /* gradient palette */
  .gbg-primary   { background: linear-gradient(135deg,#4A90E2,#6C63FF); }
  .gbg-success   { background: linear-gradient(135deg,#11998e,#38ef7d); }
  .gbg-info      { background: linear-gradient(135deg,#00b4db,#0083b0); }
  .gbg-warning   { background: linear-gradient(135deg,#f7971e,#ffd200); }
  .gbg-danger    { background: linear-gradient(135deg,#e53935,#e35d5b); }
  .gbg-secondary { background: linear-gradient(135deg,#6c757d,#adb5bd); }
  .gbg-dark      { background: linear-gradient(135deg,#2c3e50,#4ca1af); }

  /* section title */
  .feat-section-title {
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    margin-bottom: 0.85rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-transform: uppercase;
  }
  .feat-section-title::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg,currentColor 0%, transparent 100%);
    opacity: 0.25;
    border-radius: 2px;
  }

  /* open button on back */
  .flip-open-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.22);
    color: #fff;
    border: 1.5px solid rgba(255,255,255,0.55);
    border-radius: 30px;
    padding: 7px 20px;
    font-size: 0.82rem;
    font-weight: 600;
    text-decoration: none;
    margin-top: 0.75rem;
    backdrop-filter: blur(6px);
    transition: background 0.25s ease, transform 0.2s ease;
    letter-spacing: 0.04em;
  }
  .flip-open-btn:hover {
    background: rgba(255,255,255,0.38);
    transform: scale(1.06);
    color: #fff;
  }

  /* back decorative circle */
  .flip-back::before {
    content: '';
    position: absolute;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: rgba(255,255,255,0.08);
    top: -30px;
    right: -30px;
    pointer-events: none;
  }
  .flip-back::after {
    content: '';
    position: absolute;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(255,255,255,0.06);
    bottom: -10px;
    left: -10px;
    pointer-events: none;
  }

  /* Premium Tab Styles */
  .nav-tab-button {
    background: white;
    border: 1px solid rgba(0,0,0,0.08);
    color: #4a5568;
    padding: 10px 22px;
    border-radius: 30px;
    font-weight: 600;
    font-size: 0.88rem;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
    cursor: pointer;
  }
  .nav-tab-button:hover {
    background: #f7fafc;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.06);
  }
  .nav-tab-button.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white !important;
    border-color: transparent;
    box-shadow: 0 6px 15px rgba(102, 126, 234, 0.4);
  }

  /* Search component */
  .search-container {
    position: relative;
    max-width: 500px;
    margin: 0 auto 25px;
  }
  .search-input {
    width: 100%;
    padding: 12px 20px 12px 48px;
    border-radius: 30px;
    border: 2px solid rgba(102, 126, 234, 0.2);
    background: white;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0,0,0,0.02);
  }
  .search-input:focus {
    border-color: #667eea;
    outline: none;
    box-shadow: 0 4px 18px rgba(102, 126, 234, 0.12);
  }
  .search-icon {
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
    font-size: 1.15rem;
  }

  /* Stat cards */
  .stat-card-gradient {
    border: none;
    border-radius: 18px;
    overflow: hidden;
    position: relative;
    color: white;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  }
  .stat-card-gradient:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  }
  .stat-card-gradient::after {
    content: '';
    position: absolute;
    width: 130px;
    height: 130px;
    border-radius: 50%;
    background: rgba(255,255,255,0.06);
    top: -30px;
    right: -30px;
    pointer-events: none;
  }
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
  }

  .activity-log-item {
    transition: all 0.2s ease;
    border-radius: 10px;
  }
  .activity-log-item:hover {
    background-color: #f7fafc;
    transform: translateX(4px);
  }

  /* Admin modules */
  .admin-module-card {
    transition: all 0.3s ease;
    cursor: pointer;
  }
  .admin-module-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.08) !important;
  }
`;

// Animated department card
const DeptCard = ({ dept, config, index }) => {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), index * 60);
    return () => clearTimeout(t);
  }, [index]);

  const colorMap = {
    primary:   { bg: '#0d6efd', light: '#e8f0fe', text: '#0d47a1' },
    success:   { bg: '#198754', light: '#e6f4ea', text: '#155724' },
    warning:   { bg: '#ffc107', light: '#fff8e1', text: '#7d5a00' },
    info:      { bg: '#0dcaf0', light: '#e0f7fa', text: '#006064' },
    secondary: { bg: '#6c757d', light: '#f0f0f0', text: '#3d3d3d' },
    danger:    { bg: '#dc3545', light: '#fdecea', text: '#7b1a23' },
  };
  const clr = colorMap[config.color] || colorMap.primary;

  return (
    <div
      className="col-md-4 col-6 mb-3"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.35s ease ${index * 0.05}s, transform 0.35s ease ${index * 0.05}s`,
      }}
    >
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: hovered ? clr.bg : clr.light,
          borderRadius: '16px',
          padding: '18px 12px',
          textAlign: 'center',
          cursor: 'default',
          boxShadow: hovered
            ? `0 10px 25px rgba(0,0,0,0.15), 0 4px 10px ${clr.bg}44`
            : '0 2px 8px rgba(0,0,0,0.05)',
          transform: hovered ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
          transition: 'all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1)',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: hovered ? 'rgba(255,255,255,0.22)' : clr.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 10px',
            boxShadow: hovered ? '0 0 0 5px rgba(255,255,255,0.15)' : 'none',
            transition: 'all 0.25s',
          }}
        >
          <i
            className={`bi ${config.icon}`}
            style={{ fontSize: '1.25rem', color: '#fff' }}
          />
        </div>

        <h6
          style={{
            fontWeight: 700,
            color: hovered ? '#fff' : clr.text,
            marginBottom: 6,
            fontSize: '0.85rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            transition: 'color 0.25s',
          }}
          title={dept.dept_name}
        >
          {dept.dept_name}
        </h6>

        <span
          style={{
            display: 'inline-block',
            background: hovered ? 'rgba(255,255,255,0.22)' : '#fff',
            color: hovered ? '#fff' : clr.text,
            border: `1px solid ${hovered ? 'rgba(255,255,255,0.4)' : clr.bg + '33'}`,
            borderRadius: 20,
            fontSize: '0.72rem',
            fontWeight: 600,
            padding: '2px 10px',
            transition: 'all 0.25s',
          }}
        >
          {dept.employee_count} employees
        </span>
      </div>
    </div>
  );
};

/* ─── Auto-rotate Menu Cards ─── */
function useAutoRotate(gridRef, isActive) {
  useEffect(() => {
    if (!isActive) return;
    const el = gridRef.current;
    if (!el) return;

    const cards = Array.from(el.querySelectorAll('.flip-card'));
    const startTimes = cards.map(() => performance.now() - Math.random() * 4000);
    const IDLE_SPEED = 0.015; // degrees per ms

    let animating = true;
    const angles = new Array(cards.length).fill(0);
    const hovering = new Set();
    let rafId;

    const tick = (now) => {
      if (!animating) return;
      cards.forEach((card, i) => {
        if (hovering.has(i)) return;
        angles[i] = ((now - startTimes[i]) * IDLE_SPEED) % 360;
        card.style.transform = `rotateY(${angles[i]}deg)`;
      });
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    const listeners = cards.map((card, i) => {
      const scene = card.parentElement;
      const enter = () => {
        hovering.add(i);
        card.style.transition = 'transform 0.4s cubic-bezier(0.4,0.2,0.2,1)';
        card.style.transform = 'rotateY(180deg)';
      };
      const leave = () => {
        hovering.delete(i);
        card.style.transition = 'transform 0.4s cubic-bezier(0.4,0.2,0.2,1)';
        startTimes[i] = performance.now() - (angles[i] / IDLE_SPEED);
      };
      scene.addEventListener('mouseenter', enter);
      scene.addEventListener('mouseleave', leave);
      return { scene, enter, leave };
    });

    return () => {
      animating = false;
      cancelAnimationFrame(rafId);
      listeners.forEach(({ scene, enter, leave }) => {
        scene.removeEventListener('mouseenter', enter);
        scene.removeEventListener('mouseleave', leave);
      });
    };
  }, [gridRef, isActive]);
}

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef(null);

  // Tab State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list' for admin

  // API states
  const [dashboardData, setDashboardData] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    newHires: 0,
    departments: 0,
    departmentData: [],
    recentActivities: [],
    statusDistribution: [],
    attendanceGraphData: [],
  });
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  const [userPermissions, setUserPermissions] = useState({
    can_view_settings: false,
    can_edit_settings: false,
    can_manage_salary_templates: false,
    can_manage_employee_settings: false,
    can_manage_attendance_settings: false,
    loaded: false
  });
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Auto-rotate hook triggered for menu items
  useAutoRotate(gridRef, activeTab === 'menu');

  // Sync route path to active tab
  useEffect(() => {
    const path = location.pathname;
    if (path === '/menu') {
      setActiveTab('menu');
    } else if (path === '/admin-panel') {
      setActiveTab('admin');
    } else {
      setActiveTab('dashboard');
    }
  }, [location.pathname]);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    if (tabName === 'dashboard') {
      navigate('/dashboard');
    } else if (tabName === 'menu') {
      navigate('/menu');
    } else if (tabName === 'admin') {
      navigate('/admin-panel');
    }
  };

  // Fetch Dashboard Stats & Admin permissions
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoadingDashboard(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const response = await axios.get(`${API_BASE}/dashboard.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  const fetchUserPermissions = useCallback(async () => {
    try {
      setLoadingPermissions(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions(prev => ({ ...prev, loaded: true }));
        return;
      }
      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.permissions) {
        const adminPermissions = response.data.permissions.find(
          p => p.module_name === 'AdminPanel'
        );
        if (adminPermissions) {
          setUserPermissions({
            can_view_settings: Boolean(adminPermissions.can_view),
            can_edit_settings: Boolean(adminPermissions.can_edit),
            can_manage_salary_templates: Boolean(adminPermissions.can_add),
            can_manage_employee_settings: Boolean(adminPermissions.can_edit),
            can_manage_attendance_settings: Boolean(adminPermissions.can_delete),
            loaded: true
          });
        } else {
          setUserPermissions({
            can_view_settings: false,
            can_edit_settings: false,
            can_manage_salary_templates: false,
            can_manage_employee_settings: false,
            can_manage_attendance_settings: false,
            loaded: true
          });
        }
      } else {
        setUserPermissions(prev => ({ ...prev, loaded: true }));
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setUserPermissions(prev => ({ ...prev, loaded: true }));
    } finally {
      setLoadingPermissions(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
      fetchUserPermissions();
    }
  }, [user, fetchDashboardData, fetchUserPermissions]);

  /* ─── Menu Categories Configuration (Merged) ─── */
  const featureCategories = [
    {
      category: 'Employee Management',
      icon: 'bi-people',
      accent: '#4A90E2',
      items: [
        { title: 'Employee List',       icon: 'bi-people',            description: 'Manage all employees',                      path: '/employees',           color: 'primary'   },
        { title: 'Leave Types',         icon: 'bi-calendar2-week',    description: 'Manage types of leaves',                    path: '/leave-types',         color: 'info'      },
        { title: 'Leave Master',        icon: 'bi-calendar-check',    description: 'Manage employee leaves',                    path: '/leave-master',        color: 'success'   },
        { title: 'Leave Requests',      icon: 'bi-calendar-event',    description: 'View and manage leave applications',        path: '/leave-request',       color: 'warning'   },
        { title: 'Leave Ledger',        icon: 'bi-journal-check',     description: 'View employee leave balances & history',    path: '/leave-ledger',        color: 'primary'   },
        { title: 'Permission Requests', icon: 'bi-clock',             description: 'Manage permission requests',                path: '/permission-requests', color: 'info'      },
        { title: 'Onduty Requests',     icon: 'bi-briefcase',         description: 'Manage onduty requests',                    path: '/onduty-requests',     color: 'secondary' },
        { title: 'Pay Components',      icon: 'bi-calculator',        description: 'Manage salary components',                  path: '/pay-components',      color: 'warning'   },
      ]
    },
    {
      category: 'Attendance & Shifts',
      icon: 'bi-calendar-check',
      accent: '#11998e',
      items: [
        { title: 'Attendance',          icon: 'bi-calendar-check',      description: 'Manage attendance records',              path: '/attendance',          color: 'success'   },
        { title: 'Attendance Status',   icon: 'bi-list-check',          description: 'Manage attendance status types',         path: '/att-status',          color: 'info'      },
        { title: 'Attendance Logs',     icon: 'bi-clock-history',       description: 'View imported attendance logs',          path: '/attendance-logs',     color: 'secondary' },
        { title: 'Mobile Punch Logs',   icon: 'bi-phone',               description: 'View mobile attendance punch records',   path: '/mobile-punch-logs',   color: 'info'      },
        { title: 'Face Descriptors',    icon: 'bi-person-bounding-box', description: 'Manage employee face descriptors',       path: '/face-descriptor',     color: 'primary'   },
        { title: 'Shift Management',    icon: 'bi-clock-history',       description: 'Manage work shifts and schedules',       path: '/shifts',              color: 'primary'   },
        { title: 'Holiday Management',  icon: 'bi-calendar-event',      description: 'Manage company holidays',                path: '/holidays',            color: 'warning'   },
        { title: 'Layoff Register',     icon: 'bi-person-x',            description: 'Manage employee layoff records',         path: '/layoff',              color: 'danger'    },
        { title: 'Attendance Policy',   icon: 'bi-file-earmark-text',   description: 'View attendance policies',               path: '/policies',            color: 'secondary' },
        { title: 'Device',              icon: 'bi-hdd',                 description: 'Manage devices',                         path: '/device',              color: 'dark'      },
        { title: 'Downloads',           icon: 'bi-cloud-download',      description: 'Download utilities',                     path: '/download',            color: 'primary'   },
      ]
    },
    {
      category: 'Payroll Management',
      icon: 'bi-cash-coin',
      accent: '#a8329b',
      items: [
        { title: 'Process Payroll',    icon: 'bi-cash-coin',           description: 'Run and manage monthly employee payroll',  path: '/payroll',          color: 'success'   },
        { title: 'Payroll Approval',   icon: 'bi-check-circle',        description: 'Review and approve payroll processes',     path: '/payroll-approval', color: 'info'      },
        { title: 'Payroll Export',     icon: 'bi-cloud-download',      description: 'Export payroll data to spreadsheets',      path: '/payroll-export',   color: 'primary'   },
        { title: 'Statutory Reports',  icon: 'bi-file-earmark-text',   description: 'Generate PF, ESI & statutory reports',      path: '/statutory-report', color: 'warning'   },
        { title: 'Pay Register',       icon: 'bi-journal-text',        description: 'View and manage payroll registers',        path: '/payroll/register', color: 'secondary' },
        { title: 'Pay Slips',          icon: 'bi-file-earmark-person', description: 'Generate and view employee payslips',      path: '/payroll/payslip',  color: 'success'   },
      ]
    },
    {
      category: 'System Management',
      icon: 'bi-gear',
      accent: '#e53935',
      items: [
        { title: 'Admin Panel',     icon: 'bi-sliders',     description: 'Company settings and configuration',    path: '/admin-panel',     color: 'danger' },
        { title: 'User Management', icon: 'bi-person-gear', description: 'Manage system users and permissions',   path: '/user-management', color: 'dark'   },
      ]
    },
    {
      category: 'Account & Settings',
      icon: 'bi-person',
      accent: '#00b4db',
      items: [
        { title: 'Profile',       icon: 'bi-person',          description: 'View and edit your profile',      path: '/company-settings', color: 'primary'   },
        { title: 'Settings',      icon: 'bi-gear',            description: 'App settings and preferences',    path: '/admin-panel',      color: 'secondary' },
        { title: 'Notifications', icon: 'bi-bell',            description: 'Manage your notifications',       action: () => alert('Notifications feature coming soon!'), color: 'warning' },
        { title: 'Help & Support',icon: 'bi-question-circle', description: 'Get help and support',              action: () => alert('Help feature coming soon!'),          color: 'info' },
        { title: 'About',         icon: 'bi-info-circle',     description: 'About this application',          action: () => alert('HRMS v1.0 - Employee Management System'), color: 'success' },
      ]
    }
  ];

  // Filtering modules/features based on search query
  const filteredCategories = featureCategories.map(cat => {
    const query = searchQuery.toLowerCase();
    const isCategoryMatch = cat.category.toLowerCase().includes(query);
    const items = cat.items.filter(item =>
      isCategoryMatch ||
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      (item.path && item.path.toLowerCase().includes(query)) ||
      (item.path && item.path.toLowerCase().replace('-', '').includes(query))
    );
    return { ...cat, items };
  }).filter(cat => cat.items.length > 0);

  /* ─── Admin Modules Configuration ─── */
  const adminModules = [
    {
      id: 'company-settings',
      title: 'Company Settings',
      description: 'Configure company-level policies and rules',
      icon: 'bi-gear',
      color: 'primary',
      handler: () => navigate('/company-settings'),
      features: ['PF Registration', 'ESI Registration', 'Income Tax TAN', 'Contact Details']
    },
    {
      id: 'employee-settings',
      title: 'Employee Settings',
      description: 'Manage employee settings',
      icon: 'bi-person-gear',
      color: 'danger',
      handler: () => navigate('/salary-settings'),
      features: ['Onboarding', 'Document Types', 'Categories', 'Grade Settings']
    },
    {
      id: 'pay-settings',
      title: 'Pay Components Settings',
      description: 'Manage pay structures and compensation',
      icon: 'bi-cash-coin',
      color: 'success',
      handler: () => navigate('/pay-components'),
      features: ['Pay Grades', 'Allowances', 'Deductions', 'Bonus Rules']
    },
    {
      id: 'salary-template',
      title: 'Salary Templates',
      description: 'Create and manage salary groups',
      icon: 'bi-file-earmark-text',
      color: 'info',
      handler: () => navigate('/salary-templates'),
      features: ['Template Design', 'Component Setup', 'Formula Builder', 'Assign Templates']
    },
    {
      id: 'salary-settings',
      title: 'Salary Settings',
      description: 'Configure salary calculation rules for Employees',
      icon: 'bi-calculator',
      color: 'warning',
      handler: () => navigate('/salary-details'),
      features: ['Calculation Rules', 'Tax Settings', 'Arrears', 'Advance Settings']
    },
    {
      id: 'leave-settings',
      title: 'Leave Settings',
      description: 'Configure leave policies and rules',
      icon: 'bi-calendar-check',
      color: 'secondary',
      handler: () => navigate('/leave-master'),
      features: ['Leave Types', 'Leave Rules', 'Encashment', 'Carry Forward']
    },
    {
      id: 'loan-settings',
      title: 'Loan Settings',
      description: 'Manage loan policies and settings',
      icon: 'bi-bank',
      color: 'dark',
      handler: () => alert('Loan Settings page will be implemented soon!'),
      features: ['Loan Types', 'Interest Rates', 'Repayment Rules', 'Approval Workflow']
    }
  ];

  /* ─── Dashboard Configuration ─── */
  const quickStats = [
    { title: 'Total Employees',    value: dashboardData.totalEmployees,  icon: 'bi-people',        color: 'primary',  change: '+0%', trend: 'neutral' },
    { title: 'Active Employees',   value: dashboardData.activeEmployees, icon: 'bi-person-check',  color: 'success',  change: '+0%', trend: 'neutral' },
    { title: 'New Hires (30 days)', value: dashboardData.newHires,       icon: 'bi-person-plus',   color: 'info',     change: '+0',  trend: 'neutral' },
    { title: 'Departments',        value: dashboardData.departments,     icon: 'bi-building',      color: 'warning',  change: '0%',  trend: 'neutral' },
  ];

  const departmentConfig = {
    'IT':          { icon: 'bi-laptop',     color: 'primary' },
    'Sales':       { icon: 'bi-cart',       color: 'success' },
    'Support':     { icon: 'bi-headset',    color: 'warning' },
    'Marketing':   { icon: 'bi-graph-up',   color: 'info' },
    'HR':          { icon: 'bi-people',     color: 'secondary' },
    'Operations':  { icon: 'bi-gear',       color: 'danger' },
    'Finance':     { icon: 'bi-cash-coin',  color: 'success' },
    'Development': { icon: 'bi-code-slash', color: 'primary' },
    'Design':      { icon: 'bi-palette',    color: 'info' },
    'Default':     { icon: 'bi-building',   color: 'primary' },
  };
  const getDepartmentConfig = (name) => departmentConfig[name] || departmentConfig.Default;

  // Attendance Trends calculations
  const attendanceStackData = (dashboardData.attendanceGraphData || []).map(item => {
    const present = Number(item.total_present);
    const totalActive = dashboardData.activeEmployees;
    const absent = totalActive > 0 ? Math.max(0, totalActive - present) : 0;
    return { name: `Day ${item.day}`, Present: present, Absent: absent };
  });

  const attCategories = attendanceStackData.map(d => d.name);
  const presentSeries = attendanceStackData.map(d => d.Present);
  const absentSeries  = attendanceStackData.map(d => d.Absent);

  /* ─── Highcharts configuration options ─── */
  const pieOptions = {
    chart: {
      type: 'pie',
      options3d: { enabled: true, alpha: 45, beta: 0 },
      backgroundColor: 'transparent',
      height: 260,
    },
    title: { text: null },
    tooltip: { pointFormat: '<b>{point.y}</b> Employees ({point.percentage:.1f}%)' },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        depth: 40,
        innerSize: '40%',
        dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}', style: { fontSize: '11px' } },
        showInLegend: true,
      },
    },
    legend: { align: 'center', verticalAlign: 'bottom' },
    series: [{
      name: 'Employees',
      data: [
        { name: 'Active',   y: dashboardData.activeEmployees, color: '#198754' },
        { name: 'Inactive', y: Math.max(0, dashboardData.totalEmployees - dashboardData.activeEmployees), color: '#dc3545' },
      ],
    }],
    credits: { enabled: false },
  };

  const areaOptions = {
    chart: {
      type: 'areaspline',
      backgroundColor: 'transparent',
      height: 310,
      style: { fontFamily: 'inherit' },
    },
    title: { text: null },
    xAxis: {
      categories: attCategories,
      gridLineWidth: 0,
      lineColor: '#e0e0e0',
      labels: { style: { fontSize: '11px', color: '#6c757d' } },
    },
    yAxis: {
      title: { text: null },
      gridLineColor: '#f0f0f0',
      labels: { style: { fontSize: '11px', color: '#6c757d' } },
    },
    tooltip: {
      shared: true,
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor: '#e0e0e0',
      shadow: { offsetX: 0, offsetY: 4, opacity: 0.08, width: 16 },
      style: { fontSize: '13px' },
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.18,
        marker: {
          enabled: true,
          radius: 5,
          symbol: 'circle',
          lineWidth: 2,
          lineColor: null,
        },
        lineWidth: 3,
      },
    },
    series: [
      {
        name: 'Present',
        data: presentSeries,
        color: '#198754',
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(25,135,84,0.35)'],
            [1, 'rgba(25,135,84,0.01)'],
          ],
        },
      },
      {
        name: 'Absent',
        data: absentSeries,
        color: '#dc3545',
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(220,53,69,0.25)'],
            [1, 'rgba(220,53,69,0.01)'],
          ],
        },
      },
    ],
    legend: { align: 'right', verticalAlign: 'top', symbolRadius: 6 },
    credits: { enabled: false },
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f5f7fb 0%, #e4eaf5 100%)',
      minHeight: '100vh',
      paddingBottom: '80px'
    }}>
      <style>{stylesCSS}</style>

      {/* ── Tabbed Master Hub Header ── */}
      <div className="container-fluid pt-3">
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)' }}>
          <div className="card-body p-2 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-2">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                <i className="bi bi-building fs-5"></i>
              </div>
              <div>
                <h6 className="fw-bold mb-0 text-dark">Vrishab HRMS</h6>
                <small className="text-muted" style={{ fontSize: '0.75rem' }}>Integrated Hub</small>
              </div>
            </div>
            
            <div className="d-flex flex-wrap gap-2">
              <button
                onClick={() => handleTabChange('dashboard')}
                className={`nav-tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
              >
                <i className="bi bi-speedometer2"></i>
                Dashboard
              </button>
              
              <button
                onClick={() => handleTabChange('menu')}
                className={`nav-tab-button ${activeTab === 'menu' ? 'active' : ''}`}
              >
                <i className="bi bi-grid-3x3-gap"></i>
                All Features
              </button>
              
              <button
                onClick={() => handleTabChange('admin')}
                className={`nav-tab-button ${activeTab === 'admin' ? 'active' : ''}`}
              >
                <i className="bi bi-sliders"></i>
                Admin Settings
              </button>
            </div>

            <div className="d-flex align-items-center gap-2 pe-2">
              <span className="badge bg-success-subtle text-success py-2 px-3 rounded-pill" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-person-check me-1"></i>
                {user?.username || 'User'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab Views Rendering ── */}
      <div className="container-fluid">
        
        {/* ── TAB 1: DASHBOARD VIEW ── */}
        {activeTab === 'dashboard' && (
          <div className="fade-in-up">
            {/* Welcome banner */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card text-white shadow-lg border-0" style={{
                  background: `linear-gradient(135deg, rgba(76,81,191,0.92) 0%, rgba(102,126,234,0.92) 100%),
                              url('https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1600')`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  borderRadius: '18px', overflow: 'hidden'
                }}>
                  <div className="card-body py-4 text-center">
                    <h1 className="mb-2" style={{ fontWeight: 800, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
                      Welcome to HRMS Dashboard
                    </h1>
                    <p className="mb-3" style={{ opacity: 0.95 }}>
                      {user ? `Hello, ${user.username}! Ready to manage your team today?` : 'Human Resource Management System'}
                    </p>
                    <div className="d-flex flex-wrap justify-content-center gap-2">
                      <button onClick={() => handleTabChange('menu')} className="btn btn-light btn-md fw-semibold shadow-sm">
                        <i className="bi bi-grid-3x3-gap me-2"></i>Browse Features
                      </button>
                      <Link to="/employees" className="btn btn-outline-light btn-md">
                        <i className="bi bi-people me-2"></i>Employees List
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="row g-3 mb-4">
              {loadingDashboard ? (
                [1, 2, 3, 4].map(item => (
                  <div key={item} className="col-md-6 col-lg-3">
                    <div className="card h-100 shadow-sm border-0 py-3">
                      <div className="card-body placeholder-glow">
                        <span className="placeholder col-6 mb-2"></span>
                        <div className="h3 placeholder col-4 d-block" style={{ height: '30px' }}></div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                quickStats.map((stat, index) => (
                  <div key={index} className="col-md-6 col-lg-3">
                    <div className={`card h-100 stat-card-gradient gbg-${stat.color}`}>
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div>
                          <h6 className="mb-2" style={{ opacity: 0.85, fontSize: '0.88rem' }}>{stat.title}</h6>
                          <h3 className="fw-bold mb-0">{stat.value}</h3>
                          <small style={{ opacity: 0.75 }}>
                            <i className="bi bi-activity me-1"></i>System Active
                          </small>
                        </div>
                        <div className="stat-icon">
                          <i className={`bi ${stat.icon}`}></i>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Visual Charts */}
            <div className="row mb-4">
              {/* Employee Status (3D Pie) */}
              <div className="col-lg-5 mb-4 mb-lg-0">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-transparent border-0 py-3">
                    <h6 className="fw-bold mb-0 text-dark">
                      <i className="bi bi-pie-chart me-2 text-primary"></i>
                      Employee Status Distribution
                    </h6>
                  </div>
                  <div className="card-body d-flex flex-column justify-content-center align-items-center">
                    {loadingDashboard ? (
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={pieOptions}
                        containerProps={{ style: { width: '100%' } }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Attendance Trends */}
              <div className="col-lg-7">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-transparent border-0 py-3 d-flex align-items-center justify-content-between">
                    <h6 className="fw-bold mb-0 text-dark">
                      <i className="bi bi-activity me-2 text-success"></i>
                      Attendance Trends (Current Month)
                    </h6>
                    <span className="badge bg-success-subtle text-success rounded-pill px-3">
                      <i className="bi bi-circle-fill me-1" style={{ fontSize: '0.5rem' }}></i>Live
                    </span>
                  </div>
                  <div className="card-body">
                    {loadingDashboard ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : attendanceStackData.length > 0 ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={areaOptions}
                        containerProps={{ style: { width: '100%' } }}
                      />
                    ) : (
                      <div className="text-center text-muted py-5">
                        <i className="bi bi-calendar-x display-4 text-light"></i>
                        <p className="mt-2">No attendance records found for this month</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Department Cards & Activities */}
            <div className="row">
              {/* Department Overview */}
              <div className="col-lg-7 mb-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-transparent border-0 py-3">
                    <h6 className="fw-bold mb-0 text-dark">
                      <i className="bi bi-diagram-3 me-2 text-info"></i>
                      Departments Structure
                    </h6>
                  </div>
                  <div className="card-body">
                    {loadingDashboard ? (
                      <div className="row text-center">
                        {[1, 2, 3].map(item => (
                          <div key={item} className="col-4">
                            <div className="placeholder-glow py-3">
                              <span className="placeholder col-6 d-block mb-2 mx-auto" style={{ height: 40, borderRadius: '50%' }}></span>
                              <span className="placeholder col-8 mx-auto"></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : dashboardData.departmentData.length > 0 ? (
                      <div className="row text-center">
                        {dashboardData.departmentData.map((dept, index) => (
                          <DeptCard
                            key={index}
                            dept={dept}
                            config={getDepartmentConfig(dept.dept_name)}
                            index={index}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-building display-4 text-light"></i>
                        <p className="mt-2">No departments found</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Activity & Quick Actions */}
              <div className="col-lg-5 mb-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-transparent border-0 py-3">
                    <h6 className="fw-bold mb-0 text-dark">
                      <i className="bi bi-clock-history me-2 text-primary"></i>
                      Recent System Activities
                    </h6>
                  </div>
                  <div className="card-body">
                    {loadingDashboard ? (
                      <div className="placeholder-glow">
                        <span className="placeholder col-10 mb-2"></span>
                        <span className="placeholder col-8"></span>
                      </div>
                    ) : dashboardData.recentActivities.length > 0 ? (
                      <div className="list-group list-group-flush">
                        {dashboardData.recentActivities.slice(0, 4).map(activity => (
                          <div key={activity.id} className="list-group-item border-0 px-0 py-2 activity-log-item">
                            <div className="d-flex align-items-center">
                              <div className={`rounded-circle bg-${activity.color || 'primary'} bg-opacity-10 text-${activity.color || 'primary'} d-flex align-items-center justify-content-center me-3`} style={{ width: 36, height: 36, flexShrink: 0 }}>
                                <i className={`bi ${activity.icon || 'bi-info-circle'}`}></i>
                              </div>
                              <div className="flex-grow-1">
                                <p className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>{activity.message}</p>
                                <small className="text-muted" style={{ fontSize: '0.72rem' }}>{activity.time}</small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-inbox display-5 text-light"></i>
                        <p className="mt-1 small">No activities logged recently</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB 2: MENU/ALL FEATURES VIEW ── */}
        {activeTab === 'menu' && (
          <div className="fade-in-up">
            {/* Search filter bar */}
            <div className="search-container">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                placeholder="Search system features (e.g. Leave, Salary, Shift)..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Categorized flip card grid */}
            <div ref={gridRef}>
              {filteredCategories.length > 0 ? (
                filteredCategories.map(({ category, icon, accent, items }) => (
                  <div key={category} className="mb-5">
                    <div className="feat-section-title" style={{ color: accent }}>
                      <i className={`bi ${icon}`} style={{ fontSize: '1rem' }}></i>
                      {category}
                    </div>

                    <div className="row g-3">
                      {items.map((feature, idx) => (
                        <div key={idx} className="col-6 col-md-4 col-lg-3">
                          <div
                            className="flip-scene"
                            style={{ animationDelay: `${idx * 40}ms` }}
                          >
                            <div className="flip-card">
                              
                              {/* Card Front */}
                              <div className="flip-front">
                                <div className={`flip-icon gbg-${feature.color || 'primary'} text-white`}>
                                  <i className={`bi ${feature.icon}`}></i>
                                </div>
                                <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.92rem' }}>
                                  {feature.title}
                                </h6>
                                <p className="text-muted mb-0" style={{ fontSize: '0.73rem', lineHeight: 1.4 }}>
                                  {feature.description}
                                </p>
                                <small className="text-muted mt-2" style={{ fontSize: '0.65rem', opacity: 0.55 }}>
                                  <i className="bi bi-arrow-repeat me-1"></i>hover to flip
                                </small>
                              </div>

                              {/* Card Back */}
                              <div className={`flip-back gbg-${feature.color || 'primary'} text-white`}>
                                <i className={`bi ${feature.icon}`} style={{ fontSize: '2.4rem', opacity: 0.9, marginBottom: '0.5rem' }}></i>
                                <h6 className="fw-bold mb-1" style={{ fontSize: '0.96rem' }}>
                                  {feature.title}
                                </h6>
                                <p style={{ fontSize: '0.72rem', opacity: 0.85, lineHeight: 1.4, margin: '0 0 10px 0' }}>
                                  {feature.description}
                                </p>
                                {feature.path ? (
                                  <Link to={feature.path} className="flip-open-btn">
                                    Open <i className="bi bi-arrow-right"></i>
                                  </Link>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (feature.action) feature.action();
                                    }}
                                    className="flip-open-btn border-0 text-white"
                                  >
                                    Open <i className="bi bi-arrow-right"></i>
                                  </button>
                                )}
                              </div>

                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-search display-3 text-light"></i>
                  <h5 className="mt-3">No modules match "{searchQuery}"</h5>
                  <p className="small">Try refining your search keyword.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB 3: ADMIN SETTINGS VIEW ── */}
        {activeTab === 'admin' && (
          <div className="fade-in-up">
            {loadingPermissions ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading permissions...</span>
                </div>
              </div>
            ) : !userPermissions.can_view_settings ? (
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body text-center py-5">
                  <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                  <h3 className="mt-3 text-warning">Access Denied</h3>
                  <p className="text-muted">You do not have permission to view administrative settings.</p>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => handleTabChange('dashboard')}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Back to Dashboard
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {/* Admin sub-header stats */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card bg-primary bg-opacity-10 border-0 p-3">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div>
                          <h5 className="fw-bold mb-1 text-primary">
                            <i className="bi bi-tools me-2"></i>System Configuration Center
                          </h5>
                          <p className="text-muted mb-0 small">
                            Configure modules, salary templates, leaves, and system wide rules.
                          </p>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setViewMode('grid')}
                          >
                            <i className="bi bi-grid-fill"></i> Grid
                          </button>
                          <button
                            className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setViewMode('list')}
                          >
                            <i className="bi bi-list-task"></i> List
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modules grid/list */}
                <div className="row g-3 mb-4">
                  {adminModules.map((module) => (
                    <div
                      key={module.id}
                      className={viewMode === 'grid' ? 'col-md-6 col-lg-4 col-xl-3' : 'col-12'}
                    >
                      <div
                        className="card h-100 border-0 shadow-sm admin-module-card"
                        onClick={module.handler}
                        style={{
                          borderLeft: `5px solid var(--bs-${module.color})`
                        }}
                      >
                        <div className="card-body d-flex flex-column justify-content-between p-3">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <div className={`rounded-circle bg-${module.color} bg-opacity-10 text-${module.color} d-flex align-items-center justify-content-center`} style={{ width: 36, height: 36 }}>
                                <i className={`bi ${module.icon} fs-5`}></i>
                              </div>
                              <h6 className="fw-bold mb-0 text-dark">{module.title}</h6>
                            </div>
                            <p className="text-muted small mb-3">{module.description}</p>
                            
                            {/* Features breakdown */}
                            <div className="d-flex flex-wrap gap-1 mb-3">
                              {module.features.map((feat, idx) => (
                                <span key={idx} className="badge bg-light text-dark small" style={{ fontSize: '0.68rem' }}>
                                  {feat}
                                </span>
                              ))}
                            </div>
                          </div>

                          <button className={`btn btn-outline-${module.color} btn-sm w-100 mt-auto`}>
                            Configure <i className="bi bi-arrow-right ms-1"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recent Activities & Quick guides */}
                <div className="row">
                  <div className="col-md-8 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                      <div className="card-body">
                        <h6 className="fw-bold text-dark mb-3">
                          <i className="bi bi-clock-history me-2"></i>
                          Recent Configuration Activities
                        </h6>
                        <div className="list-group list-group-flush">
                          <div className="list-group-item border-0 px-0 py-2">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-primary bg-opacity-10 text-primary rounded p-2"><i className="bi bi-gear"></i></div>
                              <div>
                                <h6 className="mb-0 fw-semibold" style={{ fontSize: '0.85rem' }}>Company Settings Updated</h6>
                                <p className="text-muted small mb-0">PF percentage changed from 12% to 12.5%</p>
                              </div>
                              <small className="text-muted ms-auto">2 hours ago</small>
                            </div>
                          </div>
                          <div className="list-group-item border-0 px-0 py-2">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-success bg-opacity-10 text-success rounded p-2"><i className="bi bi-calendar-check"></i></div>
                              <div>
                                <h6 className="mb-0 fw-semibold" style={{ fontSize: '0.85rem' }}>Leave Policy Added</h6>
                                <p className="text-muted small mb-0">New paternity leave policy configured</p>
                              </div>
                              <small className="text-muted ms-auto">Yesterday</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                      <div className="card-body d-flex flex-column justify-content-between">
                        <div>
                          <h6 className="fw-bold text-dark mb-2">Need Assistance?</h6>
                          <p className="text-muted small">
                            Changes applied to system settings might affect active payroll runs and attendance rules. Refer to guidelines for support.
                          </p>
                        </div>
                        <div className="alert alert-warning border-0 small mb-0 mt-3">
                          <i className="bi bi-info-circle-fill me-2"></i>
                          Ensure parameters are validated before updating.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 text-center py-3" style={{
              background: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px'
            }}>
              <p className="text-muted small mb-0">
                <i className="bi bi-heart-fill text-danger me-1"></i>
                Developed and maintained by Vrishab Software Solutions
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Home;
